import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import imageBackground from 'assets/images/backgrounds/bg-start-survey.jpeg';
import BrandLogo from 'common/components/brand-logo';
import ModalConfirm from 'common/components/modal-confirm';
import { useAppDispatch, useAppSelector } from 'common/hook';
import {
    selectSurveyImage,
    selectWelcomeTextColor,
} from 'common/theme-selectors';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetCaptureTimeStamp } from 'slices/previewLogicPageSurveySlice';
import { IPreviewSurveyState } from 'types/previewCampaign';
import { createStyleSheet } from 'utils/helpers';
import ModalShareSurvey from '../components/modal-share-survey';

interface IStartSurveyProps {
    setSurveyStep: (step: number) => void;
}
const EndSurveyFullQuota: React.FC<IStartSurveyProps> = () => {
    const [openModalShareSurvey, setOpenModalShareSurvey] = useState(false);
    const [openModalCampaignStatus, setOpenModalCampaignStatus] =
        React.useState(false);
    const me = useAppSelector((state) => state.previewLogicPageSurvey.me);
    const surveyState = useAppSelector(
        (state) => state.previewLogicPageSurvey.surveyState
    );

    const surveyImage = useAppSelector(selectSurveyImage);
    const welcomeTextColor = useAppSelector(selectWelcomeTextColor);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(resetCaptureTimeStamp());
    }, [dispatch]);

    const navigate = useNavigate();
    const closeSurvey = (campaignId: number, me: IPreviewSurveyState['me']) => {
        let urlToBack = '';
        if (me.creator) {
            urlToBack = `/admin/campaign/design/page-logic/${campaignId}`;
        } else if (me.type === 'Admin') {
            urlToBack = `/admin/dashboard`;
        }
        navigate(urlToBack);
    };

    return (
        <Box
            sx={{
                ...styles.startSurveyLayout,
                backgroundImage: `url(${surveyImage ?? imageBackground})`,
            }}
        >
            <Grid
                container
                spacing={2}
                sx={{
                    alignItems: ['flex-end'],
                    marginBottom: ['3rem', '4rem', '6rem', '8rem'],
                }}
            >
                <Grid item xs={12} md={4} sm={6}>
                    <BrandLogo />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{
                        display: ['none', 'flex', 'flex', 'flex'],
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    {me.creator && (
                        <IconButton
                            onClick={() =>
                                closeSurvey(Number(surveyState.id), me)
                            }
                            className="close-surveys"
                        >
                            <CloseIcon
                                sx={{
                                    color: (theme) =>
                                        theme.palette.common.white,
                                    width: [
                                        '2.5rem',
                                        '2rem',
                                        '2rem',
                                        '3rem',
                                        '3rem',
                                    ],
                                    height: [
                                        '2.5rem',
                                        '2rem',
                                        '2rem',
                                        '3rem',
                                        '3rem',
                                    ],
                                }}
                            />
                        </IconButton>
                    )}
                </Grid>
            </Grid>

            <Grid
                container
                spacing={2}
                sx={{
                    marginBottom: ['3rem'],
                }}
            >
                <Grid item xs={12} md={7} lg={5} xl={5}>
                    <Typography
                        variant="h4"
                        align="left"
                        sx={{
                            fontSize: [
                                '2rem',
                                '2rem',
                                '3rem',
                                '3.5rem',
                                '3.5rem',
                            ],
                            fontWeight: '900',
                            lineHeight: [
                                '2.5rem',
                                '2.5rem',
                                '3.5rem',
                                '4rem',
                                '4rem',
                            ],
                            color: welcomeTextColor,
                            marginBottom: [
                                '1rem',
                                '1rem',
                                '1rem',
                                '2rem',
                                '2rem',
                            ],
                        }}
                    >
                        {surveyState.title}
                    </Typography>
                    <Typography
                        variant="h5"
                        align="left"
                        sx={{
                            fontSize: [
                                '1.5rem',
                                '1.5rem',
                                '2rem',
                                '2.2rem',
                                '2.2rem',
                            ],
                            fontWeight: '700',
                            lineHeight: [
                                '2rem',
                                '2rem',
                                '2.5rem',
                                '3rem',
                                '3rem',
                            ],
                            color: welcomeTextColor,
                            marginBottom: ['1rem', '1rem', '1.5rem', '1.5rem'],
                        }}
                    >
                        Cảm ơn Quý khách đã dành thời gian truy cập. Hiện tại,
                        chỉ tiêu phản hồi cho khảo sát này đã được hoàn tất. Xin
                        ghi nhận sự nhiệt tình của Quý khách và kính chúc Quý
                        khách một ngày tốt lành.
                    </Typography>
                </Grid>
            </Grid>
            {openModalShareSurvey && (
                <ModalShareSurvey
                    open={openModalShareSurvey}
                    onClose={() => setOpenModalShareSurvey(false)}
                />
            )}
            {openModalCampaignStatus && (
                <ModalConfirm
                    open={true}
                    onClose={() => setOpenModalCampaignStatus(false)}
                    title="Thông báo"
                    content={
                        <Box
                            sx={{
                                fontSize: '24px',
                            }}
                        >
                            Chiến dịch khảo sát
                            <b> {`"${surveyState.title}"`}</b> đã kết thúc.
                            <br />
                            Vui lòng kiểm tra lại sau hoặc tham các chiến dịch
                            khác
                        </Box>
                    }
                />
            )}
        </Box>
    );
};
const styles = createStyleSheet({
    startSurveyLayout: {
        width: '100vw',
        minHeight: '100vh',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: ['2rem', '2rem', '4rem', '4rem'],
    },
    btnStartSurvey: {
        padding: [
            '1rem 3rem',
            '1rem 3rem',
            '1.2rem 3.2rem',
            '1.5rem 3.5rem',
            '1.5rem 1.5rem',
        ],
        minWidth: ['10rem', '10rem', '15rem', '15rem'],
        height: ['2.5rem', '2.7rem', '3rem', '4rem', '4rem'],
        borderRadius: '50px',
        fontSize: ['1rem', '1rem', '1.2rem', '1.5rem', '1.5rem'],
        fontWeight: '700',
        lineHeight: ['1.5rem', '1.5rem', '2rem'],
    },
    btnShare: {
        minWidth: ['5rem', '7rem', '10rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});
export default EndSurveyFullQuota;
